/**
 * Client-side error reporter.
 *
 * Catches unhandled JS errors and unhandled promise rejections,
 * then POSTs them to /api/v1/client-errors for server-side logging.
 *
 * - Deduplicates: same message won't be reported more than once per 30s
 * - Self-protecting: errors from the reporting endpoint itself are ignored
 * - Fire-and-forget: all reporting failures are silently swallowed
 */

const ENDPOINT = '/api/v1/client-errors';
const DEDUP_WINDOW_MS = 30_000;

const recentErrors = new Map<string, number>();

function shouldReport(message: string): boolean {
  const now = Date.now();
  const lastSeen = recentErrors.get(message);
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return false;
  recentErrors.set(message, now);
  // Prune old entries
  if (recentErrors.size > 50) {
    recentErrors.forEach((ts, key) => {
      if (now - ts > DEDUP_WINDOW_MS) recentErrors.delete(key);
    });
  }
  return true;
}

export function reportError(error: {
  message: string;
  stack?: string;
  url?: string;
  component?: string;
  metadata?: Record<string, unknown>;
}): void {
  try {
    if (!error.message) return;
    // Don't report errors about the error endpoint
    if (error.message.includes('client-errors')) return;
    if (!shouldReport(error.message)) return;

    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: error.message.slice(0, 2000),
        stack: error.stack?.slice(0, 5000),
        url: error.url || window.location.href,
        component: error.component,
        metadata: error.metadata,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never throw from the error reporter
  }
}

/** Report 5xx API errors. Called from the axios interceptor. */
export function reportApiError(status: number, url: string, detail?: string): void {
  if (status < 500) return;
  reportError({
    message: `API ${status}: ${detail || 'Internal Server Error'}`,
    url,
    component: 'axios',
    metadata: { status },
  });
}

/** Install global error handlers. Call once at app startup. */
export function initErrorReporter(): void {
  window.onerror = (message, source, lineno, colno, error) => {
    reportError({
      message: String(message),
      stack: error?.stack,
      url: source || undefined,
      component: 'window.onerror',
      metadata: { lineno, colno },
    });
  };

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const err = event.reason;
    reportError({
      message: err?.message || String(err),
      stack: err?.stack,
      component: 'unhandledrejection',
    });
  };
}
